import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { History } from 'lucide-react';

interface ActivityLogProps {
  locationId: string;
}

export function LocationActivityLog({ locationId }: ActivityLogProps) {
  const { data: activities = [], isLoading, isError } = useQuery({
    queryKey: ['charging-location-activity', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_location_activity')
        .select(`
          *,
          profiles:user_id(display_name, email)
        `)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    retry: 1,
  });

  const getActionBadge = (action: string) => {
    const badges: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
      stage_changed: { label: 'Stadium gewijzigd', variant: 'default' },
      created: { label: 'Aangemaakt', variant: 'secondary' },
      updated: { label: 'Bijgewerkt', variant: 'secondary' },
      deleted: { label: 'Gearchiveerd', variant: 'destructive' },
      assigned: { label: 'Toegewezen', variant: 'default' },
      bulk_update: { label: 'Bulk update', variant: 'default' },
      // T2.5, migratie 084 — slapend zetten haalt een Kavel uit elke
      // standaardlijst; zonder deze twee regels is achteraf niet te zien wie
      // dat deed en wanneer, want `maakWakker` wist `dormant_at`.
      dormant: { label: 'Slapend gezet', variant: 'secondary' },
      awakened: { label: 'Wakker gemaakt', variant: 'default' },
    };
    return badges[action] || { label: action, variant: 'secondary' };
  };

  if (isLoading) {
    return <div className="text-center py-4">Laden...</div>;
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">Fout bij laden van activiteiten</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5" />
          Activiteiten ({activities.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activities.map((activity: any) => {
            const actionBadge = getActionBadge(activity.action);
            return (
              <div key={activity.id} className="flex gap-4 border-l-2 border-muted pl-4 pb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={actionBadge.variant}>
                      {actionBadge.label}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(activity.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                    </span>
                  </div>
                  
                  <p className="text-sm mb-1 whitespace-pre-wrap">{activity.description}</p>
                  
                  {activity.profiles && (
                    <p className="text-xs text-muted-foreground">
                      Door: {activity.profiles.display_name || activity.profiles.email}
                    </p>
                  )}
                  
                  {activity.old_value && activity.new_value && (
                    <div className="mt-2 text-xs bg-muted p-2 rounded">
                      <span className="line-through text-muted-foreground">
                        {JSON.stringify(activity.old_value)}
                      </span>
                      {' → '}
                      <span className="font-semibold">
                        {JSON.stringify(activity.new_value)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          
          {activities.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Nog geen activiteiten
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}